import * as React from 'react';
import * as styles from './Video.css';
import { Controller } from '../controller/Controller';
import { IComposerVideo } from '../../../interfaces/IComposerVideo';
import { Helper } from '../../../Helper';
import { LocalizationService } from '../../../services/LocalizationService';
import { inject, observer } from 'mobx-react';
import { Store } from '../stores';
import { ComposerHelper } from '../helper/ComposerHelper';
import { IAMessageVideo } from '../../messages/video/MessageVideo';
import { IAComposerError } from '../placeholders/ComposerError';
import { IAComposerPlaceholder } from '../placeholders/ComposerPlaceholder';
import { ENTER_KEY, SPACEBAR_KEY } from '../../../global/Constants';

export interface IProps {
  store?: Store;
  id: string;
  video: IComposerVideo;
  index?: number;
  hideToolbox: boolean;
  showDropZones: boolean;
  isInMobileToolbox?: boolean;
  isCover?: boolean;
  onChange: (video: IComposerVideo) => void;
  onFocus?: () => void;
  onDrag?: () => void;
  onDragEnd?: () => void;
}

export interface IState {
  showController: boolean;
  dragging: boolean;
  showDropZones: boolean;
  videoFormatError: boolean;
  videoUploadError: "too big" | "failed" | "";
  uploadProgressPercentage: number;
}

@inject('store')
@observer
export class Video extends React.Component<IProps, IState> {

  private readonly localizationService: LocalizationService = new LocalizationService();

  constructor(props: IProps) {
    super(props);
    this.state = {
      showController: this.props.isInMobileToolbox ? true : false,
      dragging: false,
      showDropZones: false,
      videoFormatError: false,
      videoUploadError: "",
      uploadProgressPercentage: undefined
    }
    this.localizationService?.checkLocalizedStrings().then(() => this.forceUpdate());
    document.body.addEventListener('onmousedown', (event: any) => {
      var exceptions = '#IA_video *, #IA_toolboxContent *, #IA_OrgAssetsUploader *, #IA_StockPhotos *';
      console.log(this.props.store.activeComponent);
      if (!event.target.matches(exceptions) && this.props.store.activeComponent && this.props.store.activeComponent.id === this.props.id) {
        this.hideToolbox()
        this.props.store.activeComponent = undefined;
      }
    });
  }

  public componentWillReceiveProps(props: IProps): void {
    if (props.hideToolbox) {
      this.hideToolbox();
    }
    if (props.showDropZones) {
      this.hideToolbox()
    }
    if (this.props.hideToolbox !== props.hideToolbox && props.hideToolbox != undefined && !props.hideToolbox) {
      this.setState({ showController: true });
    }
    if (props.showDropZones !== this.props.showDropZones) {
      setTimeout(() => {
        this.setState({ showDropZones: props.showDropZones });
      }, 0);
    }
  }

  private showToolbox(): void {
    const editor = document.getElementById(this.props.id);
    const toolbox: any = editor ? editor.getElementsByClassName("ql-toolbar")[0] : undefined;
    if (toolbox) {
      toolbox.style.display = "block";
    }
    this.setState({ showController: true });
  }

  private hideToolbox(): void {
    const editor = document.getElementById(this.props.id);
    const toolbox: any = editor ? editor.getElementsByClassName("ql-toolbar")[0] : undefined;
    if (toolbox) {
      toolbox.style.display = "none";
    }
    this.setState({ showController: false });
  }

  private setStateAndUpdateMessage(file: IComposerVideo): void {
    if (file.status !== "success") {
      file.status === "too big" ? this.setState({ videoUploadError: "too big" }) : this.setState({ videoUploadError: "failed" });
    }
    file.status = "success";
    this.props.onChange(file);
  }

  private isValidStreamUrl(): boolean {
    let isValidStreamUrl = true;
    if (this.props.video?.source === "Link" && this.props.video?.url?.toLowerCase().includes(".sharepoint.com")) {
      isValidStreamUrl = false;
      if (this.props.video?.url?.toLowerCase().includes(".mp4") || this.props.video?.url?.toLowerCase().includes(".mp3") || this.props.video?.url?.toLowerCase().includes(".mov") || this.props.video?.url?.toLowerCase().includes(".webm")) {
        isValidStreamUrl = true;
      }
      if (this.props.video?.url?.toLowerCase().includes("my.sharepoint.com")) {
        isValidStreamUrl = false;
      }
    }
    return isValidStreamUrl;
  }

  // private isValidStreamEmbed(): boolean {
  //   let isValidStreamEmbed = true;
  //   if (this.props.video?.source === "Embed" && this.props.video?.embedCode && this.props.video?.embedCode?.includes(".sharepoint.com")) {
  //     isValidStreamEmbed = false;
  //   }
  //   return isValidStreamEmbed;
  // }

  private isValidVimeoLink(url: string): boolean {
    return url.includes("vimeo") && url.includes("player.vimeo.com/video");
  }

  private isValidVimeoFormat(): boolean {
    if ((this.props.video?.source === "Link" && this.props.video?.url) && Helper.isVimeoVideo(this.props.video?.url) && !this.isValidVimeoLink(this.props.video?.url)) {
      return false;
    }
    if ((this.props.video?.source === "Embed" && this.props.video?.embedCode) && Helper.isVimeoVideo(this.props.video?.embedCode) && !this.isValidVimeoLink(this.props.video?.embedCode)) {
      return false;
    }
    return true;
  }

  private getVideoStatus(): "success" | "waiting" | "error" {
    if (this.props.video?.status === "failed"
      || this.props.video?.status === "too big"
      || this.props.video?.status === "duplicate"
      || this.state.videoFormatError
      || this.state.videoUploadError !== ""
      || !this.isValidVimeoFormat()
      // || !this.isValidStreamEmbed()
      || !this.isValidStreamUrl()
    ) {
      return "error";
    }
    if ((!this.props.video?.url && !this.props.video?.embedCode) || this.props.video?.status === "waiting" || this.props.video?.status === "uploading") {
      return "waiting";
    }
    return "success";
  }

  private getPlaceholderText(): string {
    if (this.props.store.activeComponent && this.props.store.activeComponent.id === this.props.id) {
      if (this.props.video?.source === "Upload") return this.localizationService.strings.Composer_DropVideoHere;
      if (this.props.video?.source === "Embed") return this.localizationService.strings.Composer_PasteAVideoEmbedCode;
      if (this.props.video?.source === "Link") return this.localizationService.strings.Composer_PasteAVideoURL;
    }
    return this.localizationService.strings.Composer_VideoInfo;
  }

  private getErrorText(): string {
    // if (!this.isValidStreamEmbed()) {
    //   return this.localizationService.strings.Composer_VideoEmbedCodeStreamError;
    // }
    if (!this.isValidStreamUrl()) {
      if (this.props.video?.url?.toLowerCase().includes("my.sharepoint.com")) {
        return this.localizationService.strings.Composer_VideoUrlStreamFromOneDriveError;
      }
      return this.localizationService.strings.Composer_VideoUrlStreamError;
    }
    if (this.state.videoFormatError) {
      return this.localizationService.strings.Composer_VideoFormatError;
    }
    if (!this.isValidVimeoFormat()) {
      return this.localizationService.strings.Composer_VideoVimeoErrorText;
    }
    if (this.state.videoUploadError === "too big") {
      return this.localizationService.strings.Composer_VideoUploadSizeError;
    }
    return this.localizationService.strings.Composer_VideoUploadGenericError;
  }

  // private getErrorLink(): { href: string, text: string } {
  //   if (!this.isValidStreamUrl() && !this.props.video?.url?.toLowerCase().includes("my.sharepoint.com")) {
  //     return { href: "https://docs.intraactive.net/?doc=/intraactive-intranet/videos/#section-header-two-7ud6k", text: this.localizationService.strings.Composer_VideoUrlStreamErrorGuide }
  //   }
  //   return undefined;
  // }

  private videoOnClick(): void {
    setTimeout(() => {
      this.props.onFocus();
      this.showToolbox();
    }, 100);
  }

  private onUploadProgress(progressPercentage: number) {
    this.setState({ uploadProgressPercentage: progressPercentage });
  }

  private finalizeUpload() {
    this.props.video.fileRef = null;
    this.setStateAndUpdateMessage(this.props.video);
    this.props.store.isUploadingVideoOrImage = false;
    this.setState({ uploadProgressPercentage: undefined });
  }

  public render(): JSX.Element {
    const videoStatus = this.getVideoStatus();
    let showController = this.state.showController && !this.state.showDropZones;
    if (showController && this.props.isCover && !this.props.store.activeComponent?.video?.url) showController = false;
    const height = this.props.store.isMobile ? (this.state.showDropZones ? ((screen.width - 100) / 16 * 9) + 55 : (screen.width - 100) / 16 * 9) : (this.state.showDropZones ? 336 : 349);
    let videoUrl = this.props.video?.url;
    if ((location.href.includes("sharepoint.com") && this.props.video?.embedCode) || (videoUrl == undefined && this.props.video?.embedCode)) {
      videoUrl = Helper.getVideoSrcFromEmbedCode(this.props.video?.embedCode);
    }
    return (
      <div
        id={"IA_video"}
        className={[styles.IA_video, "IA_OnFocus"].join(" ")}
        style={{ height: height }}
        tabIndex={this.props.isCover ? -1 : 0}
        role={this.props.isCover ? undefined : "button"}
        onKeyDown={(event) => {
          if (!this.props.isCover && (event.key === ENTER_KEY || event.key === SPACEBAR_KEY)) {
            event.preventDefault();
            this.videoOnClick();
          }
        }}
      >
        {((this.props.store.isMobile && this.props.isInMobileToolbox) || !this.props.store.isMobile) &&
          <Controller
            visible={showController}
            highlightColor={this.props.store.highlightColor}
            onDublicate={!this.props.isInMobileToolbox && !this.props.isCover ? () => this.props.store.dublicateComponent() : undefined}
            onDelete={() => {
              if (this.props.store.activeComponent.video && this.props.store.activeComponent.video?.url) {
                this.props.store.activeComponent.video = {
                  source: "Link",
                  posterImage: {
                    source: "Upload"
                  }
                };
              } else if (!this.props.isCover) {
                this.props.store.removeComponentAtIndex(this.props.index);
                this.hideToolbox()
                this.props.store.activeComponent = undefined;
              }
              this.forceUpdate();
            }}
            onDrag={this.props.onDrag ? () => this.props.onDrag() : undefined}
            onDragEnd={this.props.onDragEnd ? () => this.props.onDragEnd() : undefined}
          />
        }
        {this.state.showDropZones && this.props.index === 0 && !this.state.showController &&
          <div
            style={{
              marginBottom: 10
            }}
            className={styles.IA_dropZones}
            onDragOver={(event) => {
              event.preventDefault();
            }}
            onDragEnter={(event) => {
              event.preventDefault();
            }}
            onDropCapture={(event) => {
              event.preventDefault();
              if (this.props.store.startIndex != undefined) {
                this.props.store.moveComponentAtIndex(0);
              } else {
                this.props.store.addComponentAtIndex(0);
              }
            }}
          />
        }
        <div style={{ width: "100%", height: "100%", float: "left" }}>
          {videoStatus === "error" &&
            <IAComposerError
              errorMessage={this.getErrorText()}
              // errorLink={this.getErrorLink()}
              onClick={() => this.videoOnClick()}
              borderRadius={5}
            />
          }
          {videoStatus === "waiting" &&
            <IAComposerPlaceholder
              id={Helper.getRandomStringKey()}
              enableUpload={this.props.video?.source === "Upload"}
              isActive={this.props.store.activeComponent && this.props.store.activeComponent.id === this.props.id}
              highlightColor={this.props.store.highlightColor}
              icon={{
                name: "Video",
                size: 40
              }}
              showSpinner={false}
              progressBarText={this.localizationService.strings.Composer_Uploading}
              progressBarPercentage={this.state.uploadProgressPercentage}
              text={this.getPlaceholderText()}
              fileType="video"
              uploadButtonText={this.localizationService.strings.Composer_SelectFromMediaLibrary}
              borderRadius={5}
              uploadFileCallback={async (file) => {
                this.props.store.isUploadingVideoOrImage = true;
                let site: string;
                let serverRelativeUrl: string;
                if (this.props.video?.organizationalAssetPath) {
                  site = this.props.video?.organizationalAssetPath.split("/")[2];
                  serverRelativeUrl = this.props.video?.organizationalAssetPath.split(`/${site}/`)[1];
                }
                ComposerHelper.uploadFileDirectlyToSharepoint(this.props.store.environment, file, this.props.store.tenant, this.props.store.token, (progress) => this.onUploadProgress(progress), site, serverRelativeUrl)
                  .then(fileUrl => {
                    setTimeout(() => {
                      // wait for progress bar animation to finish
                      this.setState({ uploadProgressPercentage: undefined });
                      this.props.video.status = "success";
                      this.props.video.url = fileUrl;
                      this.finalizeUpload();
                    }, 500);
                  }).catch(() => {
                    this.props.video.status = "failed";
                    this.finalizeUpload();
                  });
              }}
              onClick={() => this.videoOnClick()}
            />
          }
          {videoStatus === "success" &&
            <>
              <IAMessageVideo
                id={this.props.id}
                width={"100%"}
                height={"100%"}
                videoUrl={videoUrl}
                environment={this.props.store.environment}
                tenant={this.props.store.tenant}
                token={this.props.store.token}
                onUnsupportedVideo={() => this.setState({ videoFormatError: true })}
              />
              <div
                className={styles.IA_iframeOverlay}
                style={{ marginTop: -height }}
                onClick={() => this.videoOnClick()}
              />
            </>
          }
        </div>
        {this.state.showDropZones && !this.state.showController &&
          <div
            className={styles.IA_dropZones}
            style={{
              marginTop: 10
            }}
            onDragOver={(event) => {
              event.preventDefault();
            }}
            onDragEnter={(event) => {
              event.preventDefault();
            }}
            onDropCapture={(event) => {
              event.preventDefault();
              event.preventDefault();
              if (this.props.store.startIndex != undefined) {
                this.props.store.moveComponentAtIndex(this.props.index + 1);
              } else {
                this.props.store.addComponentAtIndex(this.props.index + 1);
              }
            }}
          />
        }
      </div >
    );
  }


}